import {Injectable, signal} from '@angular/core';
import {ICommonTableConfig, ICommonTableService} from '@lib-ui/common-table/models/common-table.interface';
import {BehaviorSubject} from 'rxjs';
import {CommonTableData} from '@lib-ui/common-table/models/common-table-data.class';
import {MessageService} from '@lib-services/message.service';
import {Params} from '@angular/router';
import {IPaginatedResponse} from '@lib-typing/response.interface';
import {IPrice, IPriceTableData} from '@lib-typing/price.interface';
import {PriceService} from '@lib-services/price.service';

@Injectable({
    providedIn: 'root',
})
export class PriceListTableService implements ICommonTableService<IPriceTableData> {
    public tableData$: BehaviorSubject<CommonTableData<IPriceTableData>> = new BehaviorSubject<CommonTableData<IPriceTableData>>(new CommonTableData());
    defaultFilterParams = {
        sortBy: 'createdAt:DESC',
    };
    private readonly commonTableData = new CommonTableData();
    loading = signal<boolean>(false);

    constructor(private priceListService: PriceService, private messageService: MessageService) {}

    updateData(config: ICommonTableConfig): void {
        this.loading.set(true);
        this.priceListService
            .getList(this.createSearchParams(config))
            .subscribe({
                next: response => {
                    this.tableData$.next(new CommonTableData(this.transformData(response)));
                },
                error: () => this.messageService.errorMessage('Ошибка загрузки списка'),
            })
            .add(() => this.loading.set(false));
    }

    createSearchParams(config: ICommonTableConfig): Params {
        this.commonTableData.data.length = 0;
        const { pageIndex, itemsPerPage } = config.paginatorConfig;
        const { filters } = config;

        let searchParams: Params = {
            ...this.defaultFilterParams,
            limit: itemsPerPage.toString(),
        };

        if (pageIndex) {
            searchParams = {
                ...searchParams,
                page: pageIndex.toString(),
            };
        }

        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value) {
                    let filterValue;

                    switch (key) {
                    case 'work.name':
                        filterValue = `$ilike:${value}`;
                        break;
                    default:
                        filterValue = `${value}`;
                    }
                    searchParams = {
                        ...searchParams,
                        [`filter.${key}`]: filterValue,
                    };
                }
            });
        }

        return searchParams;
    }

    transformData(response: IPaginatedResponse<IPrice>): IPaginatedResponse<IPriceTableData> {
        const transformedData = response.data.map(price => {
            return {
                id: price.id,
                createdAt: price.createdAt,
                updatedAt: price.updatedAt,
                price: price.price,
                discountPrice: price.discountPrice || '',
                duration: price.duration,
                categoryName: price.category ? price.category.name : '',
                workName: price.work ? price.work.name : '',
                externalPriceName: this.formatExternalPriceLabel(price),
            } as IPriceTableData;

        });
        // Return a new response object with transformed data
        return {
            ...response,
            data: transformedData
        };
    }

    private formatExternalPriceLabel(price: IPrice): string {
        if (!price.externalPrice) {
            return '';
        }

        const externalId = price.externalPrice.id ?? '-';
        const externalPrice = price.externalPrice.price;
        const formattedPrice = externalPrice == null ? '-' : `${externalPrice} руб`;

        return `${price.externalPrice.name} | Наш ID: ${externalId} | Прайс: ${formattedPrice}`;
    }
}
